import { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { GameScene } from '@Core/GameScene/GameScene';
import { TimerEvent } from '@Core/Scene/Scene';
import { getSymbolRoles } from '@Core/Utils/SymbolUtils';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { EShortAnimationMode, IMultiSymbolFeature } from '@GFTypes/GameStoreTypes';
import { IReelParameters } from '@GFTypes/GameStoreTypes/ReelParameters';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter, { IEventData } from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { DeferredPromise } from '@Tools/DeferredPromise';
import { Container, DestroyOptions, Graphics, Point } from 'pixi.js';
import { ReelAnimation } from './ReelAnimation';
import ReelSymbol from './ReelSymbol';
import { ESymbolAnimations } from './SymbolAnimations';
import { ESymbolRoles } from './SymbolRoles';

export default class Reel extends Container {
    public scene: GameScene;
    private readonly gameStore: GameStore;
    public readonly reelIndex: number;
    private readonly reelConfiguration: IReelParameters;
    private readonly reelSymbolIds: number[];
    private readonly extraSymbolsBottom: number = 1;
    private reelSymbols: ReelSymbol[];
    public animation: ReelAnimation;
    private shortAnimationsCondition: LeaveCondition<EGameStates>;
    public readonly skipShortPromise = new DeferredPromise();
    private trailingEvent?: TimerEvent;
    private lastUICoverageUpdate = 0;
    private pendingUICoverage = 0;

    constructor(
        scene: GameScene,
        x: number,
        y: number,
        reelSymbolIds: number[],
        reelIndex: number,
        private readonly multiSymbols?: IMultiSymbolFeature
    ) {
        super({ position: new Point(x, y) });
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, { source: LoggerSource.Game, message: 'Reel constructor' });
        this.gameStore = this.scene.game.gameStore;
        this.reelIndex = reelIndex;
        this.reelConfiguration = this.gameStore.get(EGameStoreKeys.ReelParameters);
        this.reelSymbolIds = reelSymbolIds;
        this.zIndex = ELayeringOrder.REEL;
    }

    public init(): void {
        this.addInitialSymbols(this.reelSymbolIds);
        this.animation = new ReelAnimation(this, this.scene, this.reelIndex, this.multiSymbols);
        this.shortAnimationsCondition = new LeaveCondition<EGameStates>(`waitForShortAnimationsOnReel${this.reelIndex}`, EGameStates.SPIN_STATE);
        StateManager.assignCondition(this.shortAnimationsCondition);
        this.scene.game.scaleManager.reelResized(this.reelIndex);
        EventsCenter.on(EGameEvents.RESIZE_REELS, this.onReelsResize, this);
        EventsCenter.on(EGameEvents.PRE_REEL_SPIN_END_$, this.handleReelEnded, this);
        EventsCenter.on(EGameEvents.SKIP_SHORTS, this.onSkipShorts, this);
        this.createReelMask();
    }

    public createReelMask(): void {
        const reelParameters = this.gameStore.get(EGameStoreKeys.ReelParameters);
        if (this.mask) this.mask = null;
        const scaledWidth = reelParameters.width * this.parent?.scale.x!;
        const scaledHeight = reelParameters.height * this.parent?.scale.y!;
        const globalPoint = this.toGlobal(new Point(-scaledWidth / 2, 0));
        const scissorMask = new Graphics().rect(globalPoint.x, globalPoint.y, scaledWidth * 2, scaledHeight).fill(0xffffff);
        this.mask = scissorMask;
    }

    private addInitialSymbols(reelSymbolIds: number[]): void {
        this.reelSymbols = this.createSymbols(reelSymbolIds);
        const visibleSymbols = [...this.gameStore.get(EGameStoreKeys.VisibleSymbols)];
        const newVisibleSymbols = this.getVisibleSymbols();
        visibleSymbols[this.reelIndex] = newVisibleSymbols;
        this.gameStore.set(EGameStoreKeys.VisibleSymbols, visibleSymbols);
    }

    private createSymbols(reelSymbolIds: number[]): ReelSymbol[] {
        const newSymbols: ReelSymbol[] = [];

        reelSymbolIds.forEach((symbolId, index) => {
            const { defaultSymbolLocalPositions } = this.gameStore.get(EGameStoreKeys.ReelParameters);
            const reelSymbol = this.createSymbolById(
                symbolId,
                index,
                Math.floor(defaultSymbolLocalPositions[this.reelIndex][index].x),
                Math.floor(defaultSymbolLocalPositions[this.reelIndex][index].y)
            );

            this.addChild(reelSymbol);
            newSymbols.push(reelSymbol);
        });

        return newSymbols;
    }

    public createSymbolById(symbolId: number, symbolIndex: number, symbolX: number, symbolY: number): ReelSymbol {
        const componentSymbols = this.multiSymbols?.componentSymbolIdsByMultiSymbolIds.get(symbolId);
        const isMultiSymbol = componentSymbols?.some((symbolId) => {
            const symbolRoles = getSymbolRoles(this.gameStore, symbolId);
            return symbolRoles.includes(ESymbolRoles.LOCK_AND_ROLL_DOUBLE_COIN);
        });

        const symbolSpineAsset = isMultiSymbol
            ? this.multiSymbols!.config.asset
            : this.scene.game.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === symbolId)?.asset;

        if (symbolSpineAsset) {
            return new ReelSymbol(this, this.scene, { x: symbolX, y: symbolY }, symbolSpineAsset, symbolId, this.reelIndex, symbolIndex, this.multiSymbols);
        } else {
            throw new Error(`Symbol ${symbolId} does not exist`);
        }
    }

    public updateSymbols(reelSymbolIds: number[]): void {
        const symbols = this.createSymbols(reelSymbolIds);

        for (let i = 0; i < this.reelSymbols.length; i++) {
            const newSymbol = symbols[i];
            const oldSymbol = this.reelSymbols[i];

            this.removeChild(oldSymbol);
            this.addChild(newSymbol);

            this.reelSymbols[i] = newSymbol;
        }
    }

    public getVisibleSymbols(): ReelSymbol[] {
        return this.children
            .filter((symbol): symbol is ReelSymbol => symbol instanceof ReelSymbol)
            .toSorted((symbolA, symbolB) => symbolA.y - symbolB.y)
            .slice(1, -1);
    }

    private onReelsResize(): void {
        const { width, height, reelCenters, uiMarker } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        const originXOffset = width / 2;
        const originYOffset = height / 2;
        const x = reelCenters[this.reelIndex].x - originXOffset;
        const y = reelCenters[this.reelIndex].y - originYOffset;

        this.position = new Point(x, y);
        const worldPoint = uiMarker ?? this.parent!.toGlobal(new Point(0, reelCenters[this.reelIndex].y + originYOffset));
        const maxUICoverage = (this.scene.screen.height - worldPoint.y) / this.scene.screen.height;
        this.updateUICoverage(maxUICoverage);

        this.createReelMask();
        this.scene.game.scaleManager.reelResized(this.reelIndex);
    }

    private updateUICoverage(maxUICoverage: number) {
        this.pendingUICoverage = maxUICoverage;

        const now = performance.now();
        const remaining = 50 - (now - this.lastUICoverageUpdate);

        if (remaining <= 0) {
            this.trailingEvent?.destroy();
            this.trailingEvent = undefined;

            this.lastUICoverageUpdate = now;
            this.gameStore.set(EGameStoreKeys.UIMaxScreenCoverage, maxUICoverage);
            return;
        }

        this.trailingEvent?.destroy();

        this.trailingEvent = this.scene.time.delayedCall(remaining, () => {
            this.lastUICoverageUpdate = performance.now();
            this.gameStore.set(EGameStoreKeys.UIMaxScreenCoverage, this.pendingUICoverage);
            this.trailingEvent = undefined;
        });
    }

    private handleReelEnded(data: IEventData): void {
        this.onReelEnded(data.eventIds![0]);
    }

    private async onReelEnded(reelIndex: number): Promise<void> {
        if (this.reelIndex !== reelIndex - 1) return;
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const newVisibleSymbols = this.getVisibleSymbols();
        visibleSymbols[this.reelIndex] = newVisibleSymbols;
        this.gameStore.set(EGameStoreKeys.VisibleSymbols, visibleSymbols);

        EventsCenter.emit(EGameEvents.REEL_SPIN_END_$, { eventIds: [this.reelIndex + 1] });

        await this.waitForAllShortAnimations();
        this.shortAnimationsCondition.resolve();
    }

    private async waitForAllShortAnimations(): Promise<TrackEntry[] | void> {
        const promises: ICancelablePromise[] = [];
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const reel = visibleSymbols[this.reelIndex];
        const potentialWinningSymbols = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].potentialWinningSymbols;
        const isLockAndRollMode = this.gameStore.get(EGameStoreKeys.IsLockAndRollMode);
        this.skipShortPromise.reset();
        reel.forEach((symbol, index) => {
            const ids = symbol.isMultiSymbol ? symbol.componentSymbolIds : [symbol.symbolId];
            const shortAnimationModes = ids.map(
                (id) => this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((config) => config.id === id)?.shortAnimationMode
            );
            const shouldAlwaysPlayShortAnimation = shortAnimationModes?.includes(EShortAnimationMode.ALWAYS);
            if (isLockAndRollMode && !getSymbolRoles(this.gameStore, symbol.symbolId).includes(ESymbolRoles.LOCK_AND_ROLL_COIN)) return;
            if (!shouldAlwaysPlayShortAnimation && potentialWinningSymbols && !potentialWinningSymbols[symbol.reelIndex].toReversed()[index]) return;
            if (this.scene.stickySymbols.stickySymbols?.[symbol.reelIndex]?.[index]) return;
            if (symbol.symbolAnimation.eventManager.getAnimationNameForGameEvent(ESymbolAnimations.SHORT)) {
                const finalAnimationName = symbol.isMultiSymbol ? symbol.getMultiSymbolAnimationVariant(ESymbolAnimations.SHORT) : ESymbolAnimations.SHORT;
                promises.push(symbol.symbolAnimation.waitForAnimationEvent(finalAnimationName, 'complete'));
            }
        });

        await Promise.race([Promise.all(promises.map((promise) => promise.promise)), this.skipShortPromise.promise]);

        promises.forEach((promise) => promise.cancel());
    }

    private onSkipShorts(): void {
        this.skipShortPromise.resolve();
    }

    public getVisibleSymolsWithUpperAndLower(): ReelSymbol[] {
        return this.children.filter((symbol): symbol is ReelSymbol => symbol instanceof ReelSymbol).toSorted((symbolA, symbolB) => symbolA.y - symbolB.y);
    }

    public saveHistorySymbols(): void {
        const savedHistorySymbols = [...this.gameStore.get(EGameStoreKeys.StartHistorySymbols)];
        const symbolsForTheHistory = this.getVisibleSymolsWithUpperAndLower();
        savedHistorySymbols[this.reelIndex] = symbolsForTheHistory.map((symbol) => symbol.symbolId);
        this.gameStore.set(EGameStoreKeys.StartHistorySymbols, savedHistorySymbols);
    }

    public destroy(options?: DestroyOptions): void {
        EventsCenter.off(EGameEvents.RESIZE_REELS, this.onReelsResize, this);
        EventsCenter.off(EGameEvents.PRE_REEL_SPIN_END_$, this.handleReelEnded, this);
        EventsCenter.off(EGameEvents.SKIP_SHORTS, this.onSkipShorts, this);
        StateManager.removeCondition(this.shortAnimationsCondition);
        this.reelSymbols.forEach((reelSymbol) => {
            reelSymbol.destroy({ children: true });
        });

        super.destroy(options);
    }
}
